import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ChevronLeft, ChevronRight, CalendarDays, CheckCircle2, XCircle,
  Hand, Ban, AlertTriangle, MapPin, Clock, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 15000;

const STATUS_CLASS = {
  Open:      "bg-sky-500/15 text-sky-300 border-sky-500/40",
  Claimed:   "bg-amber-500/15 text-amber-300 border-amber-500/40",
  Attended:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  Missed:    "bg-red-500/15 text-red-300 border-red-500/40",
  Cancelled: "bg-slate-700/30 text-slate-400 border-slate-700",
};

function pad(n) { return String(n).padStart(2, "0"); }
function isoDay(d) { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }
function monthMatrix(year, month /* 0-indexed */) {
  const first = new Date(year, month, 1);
  const startDow = first.getDay(); // 0=Sun
  const daysIn = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysIn; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function Calendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cursor, setCursor] = useState(() => { const d=new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [appointments, setAppointments] = useState([]);
  const [selectedDay, setSelectedDay] = useState(isoDay(new Date()));
  const [detailId, setDetailId] = useState(null);
  const [limit, setLimit] = useState({ max_claims_per_day: 0, claimed_today: 0 });

  const start = isoDay(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
  const end = isoDay(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/appointments", { params: { start, end } });
      setAppointments(data);
    } catch {}
    try {
      const { data } = await api.get("/appointments/me/limit");
      setLimit(data);
    } catch {}
  }, [start, end]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);

  const byDay = useMemo(() => {
    const m = new Map();
    for (const a of appointments) {
      if (!m.has(a.scheduled_date)) m.set(a.scheduled_date, []);
      m.get(a.scheduled_date).push(a);
    }
    for (const arr of m.values()) arr.sort((a,b) => (a.scheduled_time||"").localeCompare(b.scheduled_time||""));
    return m;
  }, [appointments]);

  const cells = monthMatrix(cursor.getFullYear(), cursor.getMonth());
  const todayIso = isoDay(new Date());
  const dayAppts = byDay.get(selectedDay) || [];

  const [waClaim, setWaClaim] = useState(null); // {appt, targets}

  const claim = async (a) => {
    try {
      const { data } = await api.post(`/appointments/${a.id}/claim`);
      toast.success("Claimed — don't forget to attend!");
      const targets = (data?.wa_targets || []).filter((t) => t.phone);
      if (targets.length > 0) {
        setWaClaim({ appt: data, targets });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to claim");
      load();
    }
  };

  const capMsg = limit.max_claims_per_day > 0
    ? `Limited to ${limit.max_claims_per_day}/day (${limit.claimed_today} claimed today)`
    : null;

  return (
    <Layout title="Calendar">
      <div className="space-y-4" data-testid="calendar-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-emerald-400" /> Calendar
            </h1>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="ghost" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth()-1, 1))}
                data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <div className="text-sm font-medium num w-[140px] text-center">
                {cursor.toLocaleString("en", { month: "long", year: "numeric" })}
              </div>
              <Button size="icon" variant="ghost" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth()+1, 1))}
                data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" className="border-slate-700 ml-1"
                onClick={() => { const d=new Date(); setCursor(new Date(d.getFullYear(),d.getMonth(),1)); setSelectedDay(isoDay(d)); }}
                data-testid="cal-today">Today</Button>
            </div>
          </div>
          {capMsg && (
            <div className="text-xs text-amber-300 flex items-center gap-1.5 px-3 py-1 rounded-full border border-amber-500/30 bg-amber-500/10">
              <AlertTriangle className="w-3.5 h-3.5" /> {capMsg}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4">
          {/* Month grid */}
          <div className="rounded-xl border hairline surface-1 overflow-hidden">
            <div className="grid grid-cols-7 text-[11px] uppercase tracking-wider text-slate-500 border-b hairline">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                <div key={d} className="px-2 py-1.5 text-center">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 auto-rows-[minmax(96px,1fr)]">
              {cells.map((cell, i) => {
                if (!cell) return <div key={i} className="border-b border-r border-slate-900/70 bg-white/[0.01]" />;
                const key = isoDay(cell);
                const appts = byDay.get(key) || [];
                const isToday = key === todayIso;
                const isSel = key === selectedDay;
                return (
                  <button key={i} type="button" onClick={() => setSelectedDay(key)}
                    className={`relative text-left border-b border-r border-slate-900/70 p-1.5 transition-colors overflow-hidden ${
                      isSel ? "bg-emerald-600/10 ring-1 ring-inset ring-emerald-500/50" : "hover:bg-white/[0.03]"
                    }`}
                    data-testid={`cal-day-${key}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs num ${isToday ? "text-emerald-300 font-semibold" : "text-slate-400"}`}>
                        {cell.getDate()}
                      </span>
                      {appts.length > 0 && (
                        <span className="text-[10px] num text-slate-500">{appts.length}</span>
                      )}
                    </div>
                    <div className="mt-1 space-y-1">
                      {appts.slice(0, 2).map((a) => (
                        <div key={a.id}
                          className={`text-[10px] truncate px-1.5 py-0.5 rounded border ${STATUS_CLASS[a.status] || STATUS_CLASS.Open}`}
                          title={`${a.scheduled_time || ""} ${a.client_company_name || a.title}`}>
                          {a.scheduled_time ? `${a.scheduled_time} ` : ""}{a.client_company_name || a.title}
                        </div>
                      ))}
                      {appts.length > 2 && (
                        <div className="text-[10px] text-slate-500 px-1">+{appts.length - 2} more</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Day pane */}
          <div className="rounded-xl border hairline surface-1 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              {(() => { const d = new Date(selectedDay); return d.toLocaleDateString("en", { weekday: "long", month: "short", day: "numeric" }); })()}
            </div>
            {dayAppts.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">No appointments on this date.</div>
            ) : dayAppts.map((a) => (
              <AppointmentCard key={a.id} appt={a} userId={user?.id} isAdmin={isAdmin}
                onOpen={() => setDetailId(a.id)}
                onClaim={() => claim(a)} />
            ))}
          </div>
        </div>

        <AppointmentDetail appointment={appointments.find((a)=>a.id===detailId)}
          onOpenChange={(v) => !v && setDetailId(null)}
          userId={user?.id} isAdmin={isAdmin}
          onChange={load} onClaim={(a) => claim(a)} />

        <ClaimWaDialog claim={waClaim} onOpenChange={() => setWaClaim(null)} />
      </div>
    </Layout>
  );
}

function ClaimWaDialog({ claim, onOpenChange }) {
  if (!claim) return null;
  const { appt, targets } = claim;
  const openWa = (t) => {
    const phone = String(t.phone || "").replace(/[^0-9]/g, "");
    if (!phone) return;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(t.message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <Dialog open={!!claim} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg" data-testid="claim-wa-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            Appointment claimed
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-300 mb-3">
          Send a quick WhatsApp to keep everyone in the loop.
          <div className="text-xs text-slate-500 mt-1">
            {appt.client_company_name || appt.title}
            {appt.scheduled_date ? ` · ${appt.scheduled_date}` : ""}
            {appt.scheduled_time ? ` ${appt.scheduled_time}` : ""}
          </div>
        </div>
        <div className="space-y-2">
          {targets.map((t, i) => (
            <div key={i} className="rounded-md border hairline surface-2 p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-sm font-medium">{t.label}</div>
                <span className="text-[10px] uppercase tracking-wider text-slate-500">{t.role}</span>
              </div>
              <div className="text-xs text-slate-400 whitespace-pre-wrap mb-2">{t.message}</div>
              <Button size="sm" onClick={() => openWa(t)} className="bg-emerald-600 hover:bg-emerald-500"
                data-testid={`wa-send-${t.role}`}>
                Send on WhatsApp
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="wa-skip">Skip for now</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AppointmentCard({ appt, userId, isAdmin, onOpen, onClaim }) {
  const canClaim = appt.status === "Open";
  const claimedByMe = appt.claimed_by === userId;
  return (
    <div className="rounded-lg border hairline surface-2 p-3 mb-2 hover:bg-white/[0.03] transition-colors"
      data-testid={`appt-${appt.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1 cursor-pointer" onClick={onOpen}>
          <div className="text-sm font-medium truncate">{appt.client_company_name || appt.title}</div>
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            {appt.scheduled_time && <span className="flex items-center gap-1 num"><Clock className="w-3 h-3" />{appt.scheduled_time}</span>}
            {appt.location && <span className="flex items-center gap-1 truncate max-w-[160px]"><MapPin className="w-3 h-3" />{appt.location}</span>}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_CLASS[appt.status]}`}>
          {appt.status}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-2 text-[11px]">
        <span className="text-slate-500">
          Set by <span className="text-slate-300">{appt.set_by_name}</span>
          {appt.claimed_by_name && <> · Claimed by <span className="text-amber-300">{appt.claimed_by_name}</span></>}
        </span>
        {canClaim && (
          <Button size="sm" onClick={onClaim} className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs"
            data-testid={`claim-${appt.id}`}>
            <Hand className="w-3 h-3 mr-1" /> Claim
          </Button>
        )}
        {claimedByMe && appt.status === "Claimed" && (
          <span className="text-[10px] text-emerald-400">You claimed this</span>
        )}
      </div>
    </div>
  );
}

function AppointmentDetail({ appointment, onOpenChange, userId, isAdmin, onChange, onClaim }) {
  const [notes, setNotes] = useState("");
  useEffect(() => { setNotes(appointment?.outcome_notes || ""); }, [appointment?.id]); // eslint-disable-line
  if (!appointment) return null;
  const canClaim = appointment.status === "Open";
  const isOwner = appointment.claimed_by === userId || appointment.set_by === userId;
  const canOutcome = (isOwner || isAdmin) && appointment.status === "Claimed";
  const canDelete = isAdmin;

  const setOutcome = async (status) => {
    try {
      await api.post(`/appointments/${appointment.id}/outcome`, { status, outcome_notes: notes });
      toast.success(`Marked as ${status}`);
      onChange?.(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async () => {
    try {
      await api.delete(`/appointments/${appointment.id}`);
      toast.success("Deleted");
      onChange?.(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={!!appointment} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="truncate">{appointment.client_company_name || appointment.title}</span>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_CLASS[appointment.status]}`}>
              {appointment.status}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs text-slate-400">
            <div><div className="text-[10px] uppercase text-slate-500">Date</div><div className="text-slate-200 num">{appointment.scheduled_date}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Time</div><div className="text-slate-200 num">{appointment.scheduled_time || "—"}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Location</div><div className="text-slate-200">{appointment.location || "—"}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Phone</div><div className="text-slate-200 num">{appointment.client_phone || "—"}</div></div>
          </div>
          {appointment.notes && (
            <div className="rounded border-l-2 border-slate-700 pl-3 text-xs text-slate-300 whitespace-pre-wrap">
              {appointment.notes}
            </div>
          )}
          <div className="text-[11px] text-slate-500">
            Set by <span className="text-slate-300">{appointment.set_by_name}</span>
            {appointment.claimed_by_name && <> · Claimed by <span className="text-amber-300">{appointment.claimed_by_name}</span></>}
          </div>
          {canOutcome && (
            <div className="pt-2">
              <Label className="text-xs text-slate-400">Outcome notes</Label>
              <Textarea value={notes} onChange={(e)=>setNotes(e.target.value)}
                className="bg-[#0a0e1a] border-slate-800 mt-1 min-h-[60px] text-xs" />
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {canDelete && (
            <Button variant="ghost" size="sm" className="text-red-400 mr-auto" onClick={del}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          )}
          {canClaim && (
            <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={() => onClaim(appointment)}>
              <Hand className="w-4 h-4 mr-1" /> Claim
            </Button>
          )}
          {canOutcome && (
            <>
              <Button variant="outline" className="border-slate-700" onClick={() => setOutcome("Cancelled")}>
                <Ban className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button variant="outline" className="border-red-500/40 text-red-300" onClick={() => setOutcome("Missed")}
                data-testid="outcome-missed">
                <XCircle className="w-4 h-4 mr-1" /> Missed
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={() => setOutcome("Attended")}
                data-testid="outcome-attended">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Attended
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Exportable dialog for use from the Clients page to schedule an appointment on a client. */
export function ScheduleAppointmentDialog({ open, onOpenChange, client, onDone }) {
  const [date, setDate] = useState(() => isoDay(new Date()));
  const [time, setTime] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  useEffect(() => { if (open) { setDate(isoDay(new Date())); setTime(""); setLocation(""); setNotes(""); } }, [open]);
  const submit = async () => {
    if (!client || !date) return;
    try {
      await api.post("/appointments", {
        client_id: client.id,
        client_company_name: client.company_name,
        client_phone: client.phone || "",
        title: `${client.company_name} visit`,
        scheduled_date: date,
        scheduled_time: time,
        location,
        notes,
      });
      toast.success("Appointment scheduled — now visible on the calendar");
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle>Schedule appointment — {client?.company_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Date</Label>
              <Input type="date" value={date} onChange={(e)=>setDate(e.target.value)}
                className="bg-[#0a0e1a] border-slate-800 mt-1 num" data-testid="appt-date" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Time (optional)</Label>
              <Input type="time" value={time} onChange={(e)=>setTime(e.target.value)}
                className="bg-[#0a0e1a] border-slate-800 mt-1 num" data-testid="appt-time" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Location</Label>
            <Input value={location} onChange={(e)=>setLocation(e.target.value)}
              placeholder={client?.city || "Site address / meeting point"}
              className="bg-[#0a0e1a] border-slate-800 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes</Label>
            <Textarea value={notes} onChange={(e)=>setNotes(e.target.value)}
              placeholder="Who to meet, context, expected outcome…"
              className="bg-[#0a0e1a] border-slate-800 mt-1 min-h-[70px] text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={submit}
            data-testid="appt-submit">Schedule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
